/* eslint-disable no-restricted-globals */
import axios from 'axios';
import objectHash from 'object-hash';
import { isXPairedDeviceHeaderSupported } from './isXPairedDeviceHeaderSupported';
import { DEFAULT_RETRY_COUNT, DEFAULT_RETRY_DELAY } from './retryAPICalls';
import { injectedStore } from '../../ConfigureStore';
import { isConnect } from '../../../utils/isConnect';
import { isWeb } from '../../../helpers/isWeb';
import { APICallArgs } from '../../../@types';
import { emitUnauthorized } from '../../../globals/AuthEvents';

type APP_CLIENT = 'web' | 'mobile' | 'connect';

const getApplicationClient = (): APP_CLIENT =>
  isConnect() ? 'connect' : isWeb() ? 'web' : 'mobile';

export const makeAPICall = Object.freeze(
  async ({
    method = 'GET',
    url,
    params,
    data,
    headers,
    timeout = 120 * 1000,
    checkModified = !isWeb(),
    basicAuth,
  }: APICallArgs): Promise<{
    headers?: any;
    data?: any;
    notModified?: any;
    error?: any;
  }> => {
    const state = injectedStore.getState();
    try {
      const { apiBaseURL, apiVersion, xBrandId, appVersion } = state.config;
      const requestConfig: any = {
        method,
        baseURL: apiBaseURL,
        url,
        params,
        data,
        timeout,
        retryCount: DEFAULT_RETRY_COUNT,
        retryDelay: DEFAULT_RETRY_DELAY,
        responseType: 'json',
        headers: {
          Accept: 'application/json; charset=utf-8',
          'Content-Type': 'application/json; charset=utf-8',
          'X-API-Version': apiVersion,
          'X-APP-Version': appVersion,
          'X-Application-Client': getApplicationClient(),
          'Cache-Control': 'no-cache',
          ...headers,
        },
      };
      let auth;
      if (isConnect()) {
        auth = state.connect.userData.basicAuth;
      } else {
        auth = basicAuth || state.userData.user.basicAuth;
      }

      const {
        deviceUUID,
        fingerprint,
        platform,
        model,
        pushToken,
        pushDeviceID,
        // language: deviceLanguage,
        lastKnownLocation,
        userAgent,
      } = state.device;

      // Remove xBrandId to load all areas
      if (!params?.loadAllAreas) {
        if (params?.customBrandId) {
          requestConfig.headers['X-Brand-ID'] = params.customBrandId;
        } else if (xBrandId) {
          requestConfig.headers['X-Brand-ID'] = xBrandId;
        }
      }

      if (deviceUUID) {
        requestConfig.headers['X-Device-UUID'] = deviceUUID;
      }

      if ((isWeb() || isConnect()) && screen && navigator) {
        requestConfig.headers['X-Device-Screen-Color-Depth'] =
          screen.colorDepth;
        requestConfig.headers['X-Device-Screen-Width'] = screen.width;
        requestConfig.headers['X-Device-Screen-Height'] = screen.height;
        requestConfig.headers['X-Device-Java-Enabled'] =
          navigator.javaEnabled();
        requestConfig.headers['X-Device-Time-Zone-Offset'] =
          new Date().getTimezoneOffset();
      }

      if (fingerprint) {
        requestConfig.headers['X-Fingerprint'] = fingerprint;
      }

      if (!isWeb() && !isConnect() && platform) {
        if (platform) {
          requestConfig.headers[
            'X-Device-Platform'
          ] = `${platform.name} ${platform.version}`;
        }
      }

      if (model) {
        requestConfig.headers['X-Device-Model'] = model;
      }

      if (pushToken) {
        requestConfig.headers['X-Push-Token'] = pushToken;
      }

      if (pushDeviceID) {
        requestConfig.headers['X-Push-Device-ID'] = pushDeviceID;
      }

      // if (deviceLanguage) {
      //   requestConfig.headers['Accept-Language'] = deviceLanguage;
      // }

      if (lastKnownLocation) {
        requestConfig.headers[
          'X-Application-Geolocation'
        ] = `${lastKnownLocation.latitude},${lastKnownLocation.longitude}`;
      }

      if (userAgent) {
        requestConfig.headers['User-Agent'] = `${userAgent} React-Native`;
      }

      if (
        auth &&
        // Take out auth header for second car search / bub migration
        !(params?.customBrandId && (url === '/cars' || url === '/cars/compact'))
      ) {
        requestConfig.headers.Authorization = auth;
      }

      let lastFetchedAt;
      let requestHash;
      const isGetRequest = method.toLowerCase() === 'get';

      if (!isConnect() || (checkModified && isGetRequest)) {
        const { getHistory } = state.networkStatus;
        requestHash = objectHash(requestConfig, {
          unorderedArrays: true,
        });
        lastFetchedAt = getHistory[requestHash];

        if (lastFetchedAt) {
          requestConfig.headers['If-Modified-Since'] = lastFetchedAt;
        }
      }

      if (headers) {
        requestConfig.headers = { ...requestConfig.headers, ...headers };
      }

      if (
        (headers && headers['Accept'] === 'application/zip') ||
        (headers && headers['Content-Type'] === 'application/zip')
      ) {
        requestConfig.responseType = 'blob';
      }

      // $FlowFixMe
      const response = await axios(requestConfig);
      if (
        ['/serviceImageUrl', '/startCheckImageUrl', '/endCheckImageUrl'].some(
          (path) => url.includes(path)
        )
      ) {
        return {
          data: response?.headers?.location || response?.data.value,
        };
      }

      if (url === '/user/paymentMethods/adyen/publicKey') {
        // eslint-disable-next-line no-underscore-dangle
        return {
          data: response.request._response || response.data,
        };
      }

      if (checkModified && isGetRequest && requestHash) {
        let setGetHistory: any;
        if (isWeb()) {
          ({ setGetHistory } = await import(
            '../../store/networkStatus/Actions'
          ));
        } else {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          ({ setGetHistory } = require('../../store/networkStatus/Actions'));
        }
        injectedStore.dispatch(
          setGetHistory(requestHash, new Date().toUTCString())
        );
      }

      if (response.headers && isXPairedDeviceHeaderSupported(url)) {
        let setDevicePaired: any;
        if (isWeb()) {
          ({ setDevicePaired } = await import('../../store/device/Actions'));
        } else {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          ({ setDevicePaired } = require('../../store/device/Actions'));
        }
        injectedStore.dispatch(
          setDevicePaired(!response.headers['x-device-paired'])
        );
      }

      return {
        data: response.data,
        headers: response?.headers,
      };
    } catch (error: any) {
      if (error.headers && isXPairedDeviceHeaderSupported(url)) {
        let setDevicePaired: any;
        if (isWeb()) {
          ({ setDevicePaired } = await import('../../store/device/Actions'));
        } else {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          ({ setDevicePaired } = require('../../store/device/Actions'));
        }
        injectedStore.dispatch(
          setDevicePaired(!error.headers['x-device-paired'])
        );
      }

      if (error.response) {
        // Non-2xx response received
        const {
          data: errorData,
          status,
          headers: errorHeaders,
        } = error.response;

        // 304 received for unmodified, cached response
        if (status === 304) {
          return {
            notModified: true,
          };
        }

        // Handle HTTP_UNAUTHORIZED via global emitter (avoids middleware cycles)
        // Avoid emitting for explicit auth attempts (e.g., login),
        // otherwise listeners that call getUser() can loop on 401.
        if (status === 401) {
          const isExplicitAuthAttempt =
            Boolean(basicAuth) ||
            url === '/user/loginWithEmail' ||
            url === '/user/preLoginWithEmail' ||
            url === '/user'; // avoid loops when getUser itself 401s

          if (!isConnect() && !isExplicitAuthAttempt) emitUnauthorized();
        }

        return {
          error: {
            type: 'error-response-received',
            detail: {
              data: errorData,
              status,
              headers: errorHeaders,
            },
            stack: error,
          },
        };
      }

      if (error.request) {
        // No response received
        // instanceof XMLHttpRequest
        return {
          error: {
            type: 'no-response-received',
            detail: error.request,
            stack: error,
          },
        };
      }

      // Error setting up the request
      return {
        error: {
          type: 'request-setup-failed',
          detail: {
            message: error.message,
          },
          stack: error,
        },
      };
    }
  }
);

const frozenMakeAPICall = Object.freeze(makeAPICall);
export default frozenMakeAPICall;
